import {
	Group,
	UnstyledButton,
	createStyles,
	getStylesRef,
} from '@mantine/core';
import { getHotkeyHandler, useDisclosure, useHotkeys } from '@mantine/hooks';
import { Icon, ListBox, Text } from '@repo/foundations';
import { useCallback, useMemo } from 'react';
import {
	AUTOMATION_SHORTCUTS,
	GLOBAL_SHORTCUTS,
} from '../../../constants/shortcuts';
import type { AutomationEditAction, EditBlockStore } from '../store';
import type { AutomationEditType } from '../types';
import { requiresEditActionValue } from '../utils';
import { EditActionTypeDropdown } from './AutomationEdit/EditActionTypeDropdown';
import { EditActionValueDropdown } from './AutomationEdit/EditActionValueDropdown';

const useStyles = createStyles((theme) => ({
	target: {
		ref: getStylesRef('target'),

		border: `1px dashed ${theme.other.getColor('border/primary/default')}`,
		borderRadius: theme.radius.sm,
		padding: `${theme.spacing['5xs']} ${theme.spacing['2xs']} ${theme.spacing['5xs']} ${theme.spacing.sm}`,

		backgroundColor: theme.other.getColor('fill/primary/default'),

		'&:hover, &:focus': {
			backgroundColor: theme.other.getColor('fill/primary/hover'),
		},

		'&:active': {
			backgroundColor: theme.other.getColor('fill/primary/active'),
		},
	},
	targetText: {
		fontSize: theme.fontSizes.xs,
		lineHeight: theme.spacing.md,
	},
	targetIcon: {
		color: theme.other.getColor('icon/primary/default'),

		[`.${getStylesRef('target')} &:hover, .${getStylesRef('target')} &:focus`]:
			{
				color: theme.other.getColor('icon/primary/hover'),
			},

		[`.${getStylesRef('target')} &:active`]: {
			color: theme.other.getColor('icon/primary/active'),
		},
	},
}));

export interface AddEditActionProps {
	store: EditBlockStore;
	createdEditAction: AutomationEditAction | null;
	handleAddEditAction: (type: AutomationEditType, shouldClose: boolean) => void;
	reset: () => void;
}

export function AddEditAction({
	store,
	createdEditAction,
	handleAddEditAction,
	reset,
}: AddEditActionProps) {
	const { classes } = useStyles();
	const [opened, { toggle, close, open }] = useDisclosure();

	const handleSetEditActionValue = useCallback(
		(value: string | boolean, shouldClose: boolean, label?: string) => {
			if (!createdEditAction) {
				throw new Error('Edit action not found');
			}

			const editActionStore = store.editActions.get(
				createdEditAction.searchKey
			)!;

			if (!editActionStore) {
				throw new Error('Invalid edit state');
			}

			editActionStore.setValue(value);
			editActionStore.setDisplay(label ?? '');

			reset();

			if (shouldClose) {
				close();
			}
		},
		[close, store, reset, createdEditAction]
	);

	const onToggle = useCallback(() => {
		reset();
		toggle();
	}, [toggle, reset]);

	useHotkeys([[AUTOMATION_SHORTCUTS.TOGGLE_EDIT_ACTION[0], onToggle]]);

	const handleOnOpenChange = useCallback(
		(newOpen: boolean) => {
			if (newOpen) {
				reset();
				open();
			} else if (
				createdEditAction &&
				requiresEditActionValue(createdEditAction.type)
			) {
				// if dropdown is closed while selecting a value, delete the edit action to prevent empty value from being saved
				reset();
				close();
				store.deleteEditAction(createdEditAction.searchKey);
			} else {
				close();
			}
		},
		[createdEditAction, reset, open, close, store]
	);

	const handleEscapeKey = useCallback(
		(e: React.KeyboardEvent<HTMLElement> | KeyboardEvent) => {
			e.preventDefault();
			e.stopPropagation();
			if (store.editActions) {
				reset();
			} else {
				close();
			}
		},
		[close, store, reset]
	);

	const memoizedHotkeyHandler = useMemo(
		() => getHotkeyHandler([[GLOBAL_SHORTCUTS.ESCAPE[0], handleEscapeKey]]),
		[handleEscapeKey]
	);

	return (
		<ListBox
			opened={opened}
			onOpenChange={handleOnOpenChange}
			closeOnEscape={false}
			onKeyDown={memoizedHotkeyHandler}
			autoAdjustPosition={false}
			placement="bottom-start"
		>
			<ListBox.Target>
				<UnstyledButton
					data-testid="add-property-button"
					onClick={onToggle}
					className={classes.target}
				>
					<Group spacing={0}>
						<Text className={classes.targetText}>Add property</Text>
						<Icon name="plus" className={classes.targetIcon} />
					</Group>
				</UnstyledButton>
			</ListBox.Target>

			{opened && !createdEditAction && (
				<EditActionTypeDropdown
					store={store}
					onItemClick={handleAddEditAction}
				/>
			)}
			{opened &&
				createdEditAction &&
				requiresEditActionValue(createdEditAction.type) && (
					<EditActionValueDropdown
						store={store}
						actionSearchKey={createdEditAction.searchKey}
						onItemClick={handleSetEditActionValue}
					/>
				)}
		</ListBox>
	);
}
